import { useCallback, useSyncExternalStore, useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import type { EnergyLevel } from "@shared/schema";

export interface CustomPlaylist {
  id: string;
  name: string;
  url: string;
}

export interface PlaylistSettings {
  customPlaylists: CustomPlaylist[];
  hiddenCuratedGenres: string[];
  hiddenCustomPlaylists: string[];
}

const STORAGE_KEY_PREFIX = "myFitBeats_playlistSettings_";
const LEGACY_STORAGE_KEY = "myFitBeats_playlistSettings"; // Old non-user-scoped key

// Track current user ID for scoped storage
let currentUserId: string | null = null;

// Curated playlist type with Spotify URL
export interface CuratedPlaylist {
  value: string; // playlist ID
  label: string; // display name
  spotifyUrl: string; // actual Spotify playlist URL
}

function getStorageKey(userId: string | null): string {
  return userId ? `${STORAGE_KEY_PREFIX}${userId}` : LEGACY_STORAGE_KEY;
}

function getStoredSettings(userId?: string | null): PlaylistSettings {
  const effectiveUserId = userId !== undefined ? userId : currentUserId;
  try {
    const storageKey = getStorageKey(effectiveUserId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure all fields have safe defaults (handles old stored data missing new fields)
      return {
        customPlaylists: Array.isArray(parsed.customPlaylists) ? parsed.customPlaylists : [],
        hiddenCuratedGenres: Array.isArray(parsed.hiddenCuratedGenres) ? parsed.hiddenCuratedGenres : [],
        hiddenCustomPlaylists: Array.isArray(parsed.hiddenCustomPlaylists) ? parsed.hiddenCustomPlaylists : [],
      };
    }
  } catch {}
  return { customPlaylists: [], hiddenCuratedGenres: [], hiddenCustomPlaylists: [] };
}

function saveSettingsToLocalStorage(settings: PlaylistSettings, userId?: string | null) {
  const effectiveUserId = userId !== undefined ? userId : currentUserId;
  const storageKey = getStorageKey(effectiveUserId);
  localStorage.setItem(storageKey, JSON.stringify(settings));
  window.dispatchEvent(new CustomEvent("playlistSettingsChange"));
}

function clearLegacyStorage() {
  // Remove the old non-user-scoped storage to prevent data leakage
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}

let cachedSettings = getStoredSettings(null);

function subscribe(callback: () => void) {
  const handleChange = () => {
    cachedSettings = getStoredSettings();
    callback();
  };
  window.addEventListener("storage", handleChange);
  window.addEventListener("playlistSettingsChange", handleChange);
  window.addEventListener("playlistUserChange", handleChange);
  return () => {
    window.removeEventListener("storage", handleChange);
    window.removeEventListener("playlistSettingsChange", handleChange);
    window.removeEventListener("playlistUserChange", handleChange);
  };
}

function getSnapshot() {
  return cachedSettings;
}

function setCurrentUser(userId: string | null) {
  if (currentUserId !== userId) {
    currentUserId = userId;
    cachedSettings = getStoredSettings(userId);
    window.dispatchEvent(new CustomEvent("playlistUserChange"));
  }
}

async function syncSettingsToBackend(settings: PlaylistSettings, queryClient?: any): Promise<void> {
  try {
    await apiRequest("PATCH", "/api/user/preferences", {
      hiddenCuratedGenres: settings.hiddenCuratedGenres,
      customPlaylists: settings.customPlaylists,
    });
    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    }
  } catch (error) {
    console.error("Failed to sync playlist settings to backend:", error);
  }
}

export function usePlaylistManager() {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const syncedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  
  // Fetch curated playlists from API (only returns playlists with valid Spotify URLs)
  const { data: apiEnergyLevels } = useQuery<EnergyLevel[]>({
    queryKey: ['/api/energy-levels'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/energy-levels');
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Convert API energy levels to curated playlist format
  // Only include playlists that have actual Spotify URLs
  // Sort alphabetically by label for consistent UI
  const CURATED_PLAYLISTS: readonly CuratedPlaylist[] = 
    (apiEnergyLevels && apiEnergyLevels.length > 0)
      ? apiEnergyLevels
          .filter(level => level.spotifyUrl) // Only include if has actual URL
          .map(level => ({ 
            value: level.id, 
            label: level.name, 
            spotifyUrl: level.spotifyUrl! 
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
  
  // Legacy: still expose as ENERGY_LEVELS for backward compat
  const ENERGY_LEVELS: readonly { value: string; label: string }[] = CURATED_PLAYLISTS;

  // Track user changes and switch to user-scoped storage
  useEffect(() => {
    if (isAuthenticated && user && user.id !== lastUserIdRef.current) {
      lastUserIdRef.current = user.id;
      syncedRef.current = false;
      // Switch to user-scoped storage
      setCurrentUser(user.id);
      // Clear legacy non-user-scoped storage to prevent future data leakage
      clearLegacyStorage();
    }
    
    if (!isAuthenticated) {
      lastUserIdRef.current = null;
      syncedRef.current = false;
      // Clear user context when logged out
      setCurrentUser(null);
    }
  }, [isAuthenticated, user]);

  // Sync between server and user-scoped local storage
  useEffect(() => {
    if (isAuthenticated && user && !syncedRef.current) {
      syncedRef.current = true;
      
      const serverHiddenGenres = (user as any).hiddenCuratedGenres || [];
      const serverCustomPlaylists = (user as any).customPlaylists || [];
      
      // Get user-scoped local settings (currentUserId is already set by previous useEffect)
      const localSettings = getStoredSettings(user.id);
      
      const hasServerData = serverHiddenGenres.length > 0 || serverCustomPlaylists.length > 0;
      const hasLocalData = localSettings.hiddenCuratedGenres.length > 0 || localSettings.customPlaylists.length > 0;
      
      // Server data takes precedence - always sync from server if it has data
      // This ensures user's data from the database is authoritative
      if (hasServerData) {
        saveSettingsToLocalStorage({
          customPlaylists: serverCustomPlaylists,
          hiddenCuratedGenres: serverHiddenGenres,
          hiddenCustomPlaylists: localSettings.hiddenCustomPlaylists,
        }, user.id);
      } else if (hasLocalData) {
        // Only sync local to backend if server is empty (new user or data was cleared)
        syncSettingsToBackend(localSettings, queryClient);
      }
    }
  }, [isAuthenticated, user, queryClient]);

  const saveSettings = useCallback((newSettings: PlaylistSettings) => {
    saveSettingsToLocalStorage(newSettings);
    if (isAuthenticated) {
      syncSettingsToBackend(newSettings, queryClient);
    }
  }, [isAuthenticated, queryClient]);

  const addCustomPlaylist = useCallback((name: string, url: string) => {
    const newPlaylist: CustomPlaylist = {
      id: `custom-${Date.now()}`,
      name,
      url,
    };
    const current = getStoredSettings();
    const customPlaylists = Array.isArray(current.customPlaylists) ? current.customPlaylists : [];
    saveSettings({
      ...current,
      customPlaylists: [...customPlaylists, newPlaylist],
    });
    return newPlaylist;
  }, [saveSettings]);

  const updateCustomPlaylist = useCallback((id: string, name: string, url: string) => {
    const current = getStoredSettings();
    const customPlaylists = Array.isArray(current.customPlaylists) ? current.customPlaylists : [];
    saveSettings({
      ...current,
      customPlaylists: customPlaylists.map((p) =>
        p.id === id ? { ...p, name, url } : p
      ),
    });
  }, [saveSettings]);

  const removeCustomPlaylist = useCallback((id: string) => {
    const current = getStoredSettings();
    const customPlaylists = Array.isArray(current.customPlaylists) ? current.customPlaylists : [];
    saveSettings({
      ...current,
      customPlaylists: customPlaylists.filter((p) => p.id !== id),
    });
  }, [saveSettings]);

  const toggleCuratedVisibility = useCallback((genre: string) => {
    const current = getStoredSettings();
    const hiddenCuratedGenres = Array.isArray(current.hiddenCuratedGenres) ? current.hiddenCuratedGenres : [];
    const isHidden = hiddenCuratedGenres.includes(genre);
    
    // Prevent hiding if this would hide all energy levels
    if (!isHidden) {
      const visibleCount = ENERGY_LEVELS.filter(
        (g) => !hiddenCuratedGenres.includes(g.value) && g.value !== genre
      ).length;
      if (visibleCount === 0) {
        // At least one energy level must remain visible
        return;
      }
    }
    
    saveSettings({
      ...current,
      hiddenCuratedGenres: isHidden
        ? hiddenCuratedGenres.filter((g) => g !== genre)
        : [...hiddenCuratedGenres, genre],
    });
  }, [saveSettings, ENERGY_LEVELS]);

  const setCuratedVisibility = useCallback((genre: string, visible: boolean) => {
    const current = getStoredSettings();
    const hiddenCuratedGenres = Array.isArray(current.hiddenCuratedGenres) ? current.hiddenCuratedGenres : [];
    const isHidden = hiddenCuratedGenres.includes(genre);
    
    // Prevent hiding if this would hide all energy levels
    if (!visible && !isHidden) {
      const visibleCount = ENERGY_LEVELS.filter(
        (g) => !hiddenCuratedGenres.includes(g.value) && g.value !== genre
      ).length;
      if (visibleCount === 0) {
        // At least one energy level must remain visible
        return;
      }
    }
    
    if (visible && isHidden) {
      saveSettings({
        ...current,
        hiddenCuratedGenres: hiddenCuratedGenres.filter((g) => g !== genre),
      });
    } else if (!visible && !isHidden) {
      saveSettings({
        ...current,
        hiddenCuratedGenres: [...hiddenCuratedGenres, genre],
      });
    }
  }, [saveSettings, ENERGY_LEVELS]);

  const getVisiblePlaylists = useCallback(() => {
    const current = getStoredSettings();
    const hiddenCustom = Array.isArray(current.hiddenCustomPlaylists) ? current.hiddenCustomPlaylists : [];
    const hiddenCuratedGenres = Array.isArray(current.hiddenCuratedGenres) ? current.hiddenCuratedGenres : [];
    const customPlaylists = Array.isArray(current.customPlaylists) ? current.customPlaylists : [];
    const visibleCurated = CURATED_PLAYLISTS.filter(
      (g) => !hiddenCuratedGenres.includes(g.value)
    );
    const visibleCustom = customPlaylists
      .filter((p) => !hiddenCustom.includes(p.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {
      curated: visibleCurated,
      custom: visibleCustom,
    };
  }, [CURATED_PLAYLISTS]);

  const isCuratedVisible = useCallback((genre: string) => {
    const hiddenCuratedGenres = Array.isArray(settings.hiddenCuratedGenres) ? settings.hiddenCuratedGenres : [];
    return !hiddenCuratedGenres.includes(genre);
  }, [settings.hiddenCuratedGenres]);

  const isCustomVisible = useCallback((id: string) => {
    return !(settings.hiddenCustomPlaylists || []).includes(id);
  }, [settings.hiddenCustomPlaylists]);

  const setCustomVisibility = useCallback((id: string, visible: boolean) => {
    const current = getStoredSettings();
    const hiddenCustomPlaylists = current.hiddenCustomPlaylists || [];
    const isHidden = hiddenCustomPlaylists.includes(id);
    if (visible && isHidden) {
      saveSettings({
        ...current,
        hiddenCustomPlaylists: hiddenCustomPlaylists.filter((i) => i !== id),
      });
    } else if (!visible && !isHidden) {
      saveSettings({
        ...current,
        hiddenCustomPlaylists: [...hiddenCustomPlaylists, id],
      });
    }
  }, [saveSettings]);

  return {
    customPlaylists: settings.customPlaylists,
    hiddenCuratedGenres: settings.hiddenCuratedGenres,
    hiddenCustomPlaylists: settings.hiddenCustomPlaylists || [],
    addCustomPlaylist,
    updateCustomPlaylist,
    removeCustomPlaylist,
    toggleCuratedVisibility,
    setCuratedVisibility,
    getVisiblePlaylists,
    isCuratedVisible,
    isCustomVisible,
    setCustomVisibility,
    CURATED_PLAYLISTS,
    // Legacy alias
    CURATED_GENRES: ENERGY_LEVELS,
  };
}
